import { ArrowLeft, Package, MapPin, ShieldCheck, Banknote, Clock } from "lucide-react";
import { BottomNav } from "./bottom-nav";

interface TrocaViaHubPageProps {
  onBack: () => void;
  activeTab: "home" | "favorites";
  onTabChange: (tab: string) => void;
  favoritesCount: number;
  onMenuClick: () => void;
  onPostAdClick: () => void;
  postAdBlockedByUnverifiedEmail?: boolean;
}

const sections: {
  icon: typeof Package;
  title: string;
  body: string;
}[] = [
  {
    icon: Package,
    title: "O que é a troca via Hub",
    body:
      "Em uma troca, os dois lados enviam produtos. Com o Hub akihub, cada um posta o item para o nosso centro de recebimento — não enviam direto um para o outro. Assim a plataforma pode conferir o que chegou antes de seguir com o encaminhamento para os endereços finais. Compras comuns (um só envia) continuam com frete direto, sem passar pelo hub.",
  },
  {
    icon: MapPin,
    title: "Como é o trajeto do frete",
    body:
      "O frete é dividido em quatro trechos: do vendedor até o hub; do comprador até o hub (com o produto que ele está trocando); do hub até o comprador (produto que ele recebe); do hub até o vendedor (produto que ele recebe). No checkout você vê o resumo do percurso e a sua parte do custo.",
  },
  {
    icon: Banknote,
    title: "Quem paga o quê",
    body:
      "Cada pessoa paga a sua parcela de frete (os trechos da sua responsabilidade). Na tela de envio aparece “Sua parte do frete” com o valor que se aplica a você naquele pedido.",
  },
  {
    icon: Clock,
    title: "Dois pagamentos no mesmo pedido",
    body:
      "Em muitas trocas, um lado paga primeiro (produto e/ou frete, conforme o caso). A outra parte precisa concluir o pagamento da sua parte dentro do prazo indicado. Se o prazo passar sem pagamento, o pedido pode ser cancelado e valores já pagos reembolsados, para liberar os anúncios de novo.",
  },
  {
    icon: ShieldCheck,
    title: "Depois que os dois recebem",
    body:
      "Quando os dois produtos chegam aos destinos finais, há um período em que qualquer um pode reportar problema (por exemplo, item divergente ou avariado). Se tudo estiver certo nesse período, valores retidos em garantia — como uma diferença paga em dinheiro na troca — podem ser liberados conforme as regras da plataforma.",
  },
];

export function TrocaViaHubPage({
  onBack,
  activeTab,
  onTabChange,
  favoritesCount,
  onMenuClick,
  onPostAdClick,
  postAdBlockedByUnverifiedEmail = false,
}: TrocaViaHubPageProps) {
  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="bg-primary border-b border-primary sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-4 px-4 py-4">
            <button
              type="button"
              onClick={onBack}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors -ml-2"
              aria-label="Voltar"
            >
              <ArrowLeft className="w-6 h-6 text-white" />
            </button>
            <h1 className="text-lg font-medium text-white">Troca via Hub</h1>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <p className="text-sm text-muted-foreground leading-relaxed">
          Resumo de como funciona o envio quando a negociação é uma <strong className="text-foreground">troca</strong> e o frete é feito{" "}
          <strong className="text-foreground">via Hub akihub</strong>.
        </p>

        <div className="space-y-4">
          {sections.map(({ icon: Icon, title, body }) => (
            <section
              key={title}
              className="bg-card border border-border rounded-xl p-4 flex gap-4"
            >
              <div className="shrink-0 w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Icon className="w-5 h-5 text-primary" aria-hidden />
              </div>
              <div className="min-w-0">
                <h2 className="font-medium text-foreground mb-2">{title}</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
              </div>
            </section>
          ))}
        </div>

        <p className="text-xs text-muted-foreground border-t border-border pt-4">
          Dúvidas? Use <strong className="text-foreground">Ajuda &amp; Suporte</strong> no menu ou fale com o time pelo e-mail de contato indicado lá.
        </p>
      </div>

      <BottomNav
        activeTab={activeTab}
        onTabChange={onTabChange}
        favoritesCount={favoritesCount}
        onMenuClick={onMenuClick}
        onPostAdClick={onPostAdClick}
        postAdBlockedByUnverifiedEmail={postAdBlockedByUnverifiedEmail}
      />
    </div>
  );
}
